import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/axios';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutes total session
const WARNING_BEFORE_MS  = 5 * 60 * 1000;    // Show warning 5 minutes before expiry (at 25 minutes)

export const useSessionTimeout = ({ isAuthenticated, onLogout }) => {
  const [showWarning, setShowWarning]   = useState(false);
  const [countdown, setCountdown]       = useState(WARNING_BEFORE_MS / 1000); // seconds

  const idleTimerRef      = useRef(null);
  const warningTimerRef   = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastRefreshRef    = useRef(Date.now());

  // ── Logout ─────────────────────────────────────────────────────────────────
  const doLogout = useCallback(async () => {
    try { await api.post('/logout'); } catch (_) {}
    setShowWarning(false);
    onLogout();
  }, [onLogout]);

  // ── Stay logged in — call /refresh to extend server session ─────────
  const stayLoggedIn = useCallback(async () => {
    try {
      await api.post('/refresh');
      lastRefreshRef.current = Date.now();
    } catch (_) {}
    setShowWarning(false);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownTimerRef.current);
    resetIdleTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Reset the idle timer whenever there is user activity ─────────────────
  const resetIdleTimer = useCallback(() => {
    if (!isAuthenticated) return;

    // Silent Refresh: if user is actively using the system and it's been > 15 minutes
    // since last refresh, perform a silent refresh in the background.
    const now = Date.now();
    if (now - lastRefreshRef.current > 15 * 60 * 1000) {
      lastRefreshRef.current = now;
      api.post('/refresh').catch(() => {});
    }

    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownTimerRef.current);

    if (showWarning) setShowWarning(false);

    // Schedule the WARNING
    idleTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(WARNING_BEFORE_MS / 1000);

      // Countdown tick every second
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Schedule LOGOUT after warning period
      warningTimerRef.current = setTimeout(() => {
        doLogout();
      }, WARNING_BEFORE_MS);

    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);
  }, [isAuthenticated, showWarning, doLogout]);

  // ── Attach activity listeners ─────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetIdleTimer();

    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer(); // kick off on mount

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
      clearInterval(countdownTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { showWarning, countdown, stayLoggedIn, doLogout };
};
